'use client';

import Hls from 'hls.js';
import { useEffect, useRef } from 'react';

interface HomeHlsVideoProps {
  src: string;
  poster?: string;
  playing: boolean;
  className?: string;
}

const playVideo = (video: HTMLVideoElement | null) => {
  if (!video) return;

  video.play().catch(() => {
    // Muted autoplay should usually pass, but keeping the poster is acceptable.
  });
};

const resetVideoSource = (video: HTMLVideoElement) => {
  video.pause();
  video.removeAttribute('src');
  video.load();
};

export const HomeHlsVideo = ({
  src,
  poster,
  playing,
  className,
}: HomeHlsVideoProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const playingRef = useRef(playing);

  useEffect(() => {
    playingRef.current = playing;
  }, [playing]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let disposed = false;

    hlsRef.current?.destroy();
    hlsRef.current = null;
    resetVideoSource(video);

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
      video.load();

      if (playingRef.current) {
        playVideo(video);
      }

      return () => {
        disposed = true;
        resetVideoSource(video);
      };
    }

    if (!Hls.isSupported()) return;

    const hls = new Hls({
      backBufferLength: 0,
      maxBufferLength: 10,
      xhrSetup: xhr => {
        xhr.withCredentials = true;
      },
    });

    hlsRef.current = hls;
    hls.attachMedia(video);
    hls.on(Hls.Events.MEDIA_ATTACHED, () => {
      if (!disposed) {
        hls.loadSource(src);
      }
    });
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      if (playingRef.current) {
        playVideo(video);
      }
    });
    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (!data.fatal) return;

      if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
        hls.startLoad();
        return;
      }

      if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
        hls.recoverMediaError();
      }
    });

    return () => {
      disposed = true;
      hls.destroy();
      if (hlsRef.current === hls) {
        hlsRef.current = null;
      }
      resetVideoSource(video);
    };
  }, [src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (!playing) {
      video.pause();
      return;
    }

    playVideo(video);
  }, [playing, src]);

  return (
    <video
      ref={videoRef}
      className={className}
      poster={poster}
      muted
      playsInline
      loop
      controls={false}
      preload="auto"
      disablePictureInPicture
      aria-hidden
    />
  );
};
